import { DragEndEvent, DragMoveEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { CSS, Transform } from '@dnd-kit/utilities';
import { Cell, ColumnPinningState, Header, Row } from '@tanstack/react-table';
import { CSSProperties } from 'react';

// Type for column methods needed for pinning styles
type ColumnWithPinning = {
  getIsPinned: () => false | 'left' | 'right';
  getIsLastColumn: (position: 'left' | 'right') => boolean;
  getStart: (position: 'left' | 'right') => number;
  getSize: () => number;
  // Right-pin support (tanstack v8) — optional so lightweight test doubles and
  // older callsites that only exercise left pinning keep working.
  getAfter?: (position: 'left' | 'right') => number;
  getIsFirstColumn?: (position: 'left' | 'right') => boolean;
};

/**
 * Returns CSS styles for pinned columns (sticky positioning, shadows)
 * @param {ColumnWithPinning} column - The column to get pinning styles for
 * @return {CSSProperties} CSS properties for pinned column styling
 */
export const getPinningStyles = (column: ColumnWithPinning): CSSProperties => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === 'left' && column.getIsLastColumn('left');
  // Default to NO shadow when the column can't say whether it's first — a
  // permissive default would shadow every right-pinned column for callers
  // (and test doubles) that don't implement getIsFirstColumn.
  const isFirstRightPinnedColumn =
    isPinned === 'right' && (column.getIsFirstColumn?.('right') ?? false);

  return {
    boxShadow: isLastLeftPinnedColumn
      ? 'inset -5px 0px 6px -6px rgba(141,141,141,1)'
      : isFirstRightPinnedColumn
        ? 'inset 5px 0px 6px -6px rgba(141,141,141,1)'
        : undefined,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right:
      isPinned === 'right' ? `${column.getAfter?.('right') ?? 0}px` : undefined,
    position: isPinned ? 'sticky' : 'relative',
    width: column.getSize(),
    zIndex: isPinned ? 1 : 0,
  };
};

/**
 * Returns CSS styles for a column being dragged
 * @param {Transform | null} transform - The transform object from drag operation
 * @return {CSSProperties} CSS properties for dragging styling
 */
export const draggingStyle = (transform: Transform | null): CSSProperties => ({
  opacity: 0.8,
  transform: CSS.Translate.toString(transform),
  zIndex: 1,
});

/**
 * Handles the end of a drag-and-drop event for columns.
 * Ensures pinned and unpinned columns can only be reordered within their own group.
 * @param {DragEndEvent} event - The drag end event
 * @param {ColumnPinningState} columnPinning - Current column pinning state
 * @param {string[]} columnOrder - Current column order
 * @param {React.Dispatch<React.SetStateAction<string[]>>} setColumnOrder - Function to update column order
 * @param {React.Dispatch<React.SetStateAction<ColumnPinningState>>} setColumnPinning - Function to update column pinning
 * @param {React.MutableRefObject<string | null>} hoveredColumnIdRef - Ref to track hovered column ID
 */
export const handleDragEnd = (
  event: DragEndEvent,
  columnPinning: ColumnPinningState,
  columnOrder: string[],
  setColumnOrder: React.Dispatch<React.SetStateAction<string[]>>,
  setColumnPinning: React.Dispatch<React.SetStateAction<ColumnPinningState>>,
  hoveredColumnIdRef: React.MutableRefObject<string | null>
) => {
  const { active, over } = event;

  // Check if the drag event is valid and ensure the IDs are strings
  if (
    !active ||
    !over ||
    typeof active.id !== 'string' ||
    typeof over.id !== 'string' ||
    active.id === over.id
  ) {
    return;
  }

  const activeId = active.id;
  const overId = over.id;
  const leftPinnedColumns = columnPinning.left ?? [];

  // Determine if the dragged column and target column are pinned
  const movingPinnedColumn = leftPinnedColumns.includes(activeId);
  const targetPinnedColumn = leftPinnedColumns.includes(overId);

  // Determine if the dragged and target columns are among the unpinned columns
  const movingUnPinnedColumn = !targetPinnedColumn;
  const targetUnpinnedColumn = !movingPinnedColumn;

  // Check if both columns belong to the same group (either pinned or unpinned)
  const isColumnGroupSame =
    (movingPinnedColumn && targetPinnedColumn) ||
    (movingUnPinnedColumn && targetUnpinnedColumn);

  // If the columns belong to the same group, update the column order and pinning state
  if (isColumnGroupSame) {
    setColumnOrder((prevColumnOrder) => {
      const oldIndex = prevColumnOrder.indexOf(activeId);
      const newIndex = prevColumnOrder.indexOf(overId);
      const newColumnOrder = arrayMove(prevColumnOrder, oldIndex, newIndex);

      // Update the column pinning state based on the new column order.
      // Right pins are preserved as-is (reordering only reorders left pins).
      setColumnPinning((prevPinning) => {
        const leftPinnedColumns = prevPinning.left ?? [];
        const columnIndexMap = new Map(
          newColumnOrder.map((id, index) => [id, index])
        );

        const updatedLeftPinnedColumns = leftPinnedColumns
          .filter((id) => columnIndexMap.has(id))
          .sort((a, b) => columnIndexMap.get(a)! - columnIndexMap.get(b)!);

        return {
          left: updatedLeftPinnedColumns,
          right: prevPinning.right ?? [],
        };
      });

      return newColumnOrder;
    });
  }

  // Clear the hovered column ID after the drag ends
  hoveredColumnIdRef.current = null;
};

/**
 * Handles drag move events to track which column is being hovered over
 * @param {DragMoveEvent} event - The drag move event
 * @param {React.MutableRefObject<string | null>} hoveredColumnIdRef - Ref to track hovered column ID
 */
export const handleDragMove = (
  event: DragMoveEvent,
  hoveredColumnIdRef: React.MutableRefObject<string | null>
) => {
  const { active, over } = event;

  if (over && active.id !== over.id && typeof over.id === 'string') {
    hoveredColumnIdRef.current = over.id;
  } else {
    hoveredColumnIdRef.current = null;
  }
};

/**
 * Extracts column IDs from column definitions for initial column order
 * @param {Array<any>} columns - Array of column definitions with optional id or accessorKey property
 * @return {string[]} Array of column IDs, falling back to accessorKey when id is absent
 */
export const getInitialColumnOrder = (columns: any[]): string[] => {
  return columns
    .map((col) => col?.id ?? col?.accessorKey)
    .filter(
      (item): item is string => typeof item === 'string' && item !== undefined
    );
};

// Props interfaces for draggable components
export interface DragHeaderProps<TData, TValue> {
  header: Header<TData, TValue>;
  hoveredColumnIdRef: React.RefObject<string | null>;
}

export interface DragCellProps<TData, TValue> {
  cell: Cell<TData, TValue>;
  row: Row<TData>;
  hoveredColumnIdRef: React.RefObject<string | null>;
}
